import type { Ref } from 'vue'
// Client-only state shared by all modal/drawer instances, including nested dialogs.
const overlays: symbol[] = []
let savedOverflow = ''
export function useUiOverlay(
  open: Ref<boolean>,
  panel: Ref<HTMLElement | undefined>,
  close: () => void,
  initialFocus: () => string | undefined,
) {
  const key = Symbol('overlay')
  const layer = ref(0)
  let previousFocus: HTMLElement | null = null
  let registered = false
  const selector =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]'
  const top = () => overlays.at(-1) === key
  function portals() {
    const id = panel.value?.dataset.uiOverlayId
    return id
      ? [...document.querySelectorAll<HTMLElement>('[data-ui-overlay-owner]')].filter(
          (element) => element.dataset.uiOverlayOwner === id,
        )
      : []
  }
  function contains(node: Node | null) {
    return panel.value?.contains(node) || portals().some((element) => element.contains(node))
  }
  function items() {
    return [
      ...(panel.value?.querySelectorAll<HTMLElement>(selector) ?? []),
      ...portals().flatMap((element) => [...element.querySelectorAll<HTMLElement>(selector)]),
    ].filter(
      (element) =>
        !element.closest('[inert], [hidden], [aria-hidden="true"]') &&
        element.getClientRects().length > 0,
    )
  }
  function focusFirst() {
    const requested = initialFocus()
    ;(requested ? panel.value?.querySelector<HTMLElement>(requested) : null)?.focus()
    if (!contains(document.activeElement)) (items()[0] ?? panel.value)?.focus()
  }
  function keydown(event: KeyboardEvent) {
    if (!top() || event.defaultPrevented) return
    if (event.key === 'Escape') {
      event.preventDefault()
      close()
    }
    if (event.key !== 'Tab') return
    const controls = items()
    const first = controls[0]
    const last = controls.at(-1)
    if (!first) {
      event.preventDefault()
      panel.value?.focus()
    } else if (
      event.shiftKey &&
      (document.activeElement === first || !contains(document.activeElement))
    ) {
      event.preventDefault()
      last?.focus()
    } else if (
      !event.shiftKey &&
      (document.activeElement === last || !contains(document.activeElement))
    ) {
      event.preventDefault()
      first.focus()
    }
  }
  function focusin(event: FocusEvent) {
    if (top() && panel.value && !contains(event.target as Node)) focusFirst()
  }
  async function register() {
    if (registered) return
    registered = true
    previousFocus = document.activeElement as HTMLElement | null
    if (!overlays.length) {
      savedOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
    }
    overlays.push(key)
    layer.value = overlays.length - 1
    document.addEventListener('keydown', keydown)
    document.addEventListener('focusin', focusin)
    await nextTick()
    if (registered && top()) focusFirst()
  }
  function release() {
    if (!registered) return
    const wasTop = top()
    registered = false
    overlays.splice(overlays.indexOf(key), 1)
    document.removeEventListener('keydown', keydown)
    document.removeEventListener('focusin', focusin)
    if (!overlays.length) document.body.style.overflow = savedOverflow
    if (wasTop && previousFocus?.isConnected) previousFocus.focus()
  }
  onMounted(() => {
    watch(
      open,
      (value) => {
        if (value) void register()
        else release()
      },
      { immediate: true, flush: 'post' },
    )
  })
  onBeforeUnmount(release)
  return { layer, isTop: top }
}
